// The studio film, pinned at the top of the page while the page scrolls over
// it. It shows its file name and a running timecode. Clicking it pauses or
// plays it, with the site's cursor tooltip saying which.

const FPS = 24;
// Below this much visible film, the labels fade out before they meet.
const MIN_VISIBLE = 140;

const pad = (n) => String(n).padStart(2, "0");

function timecode(seconds) {
  const frames = Math.floor(seconds * FPS);
  const f = frames % FPS;
  const s = Math.floor(frames / FPS);
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor(s / 60) % 60)}:${pad(s % 60)}:${pad(f)}`;
}

class StudioFilmElement extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector("video");
    if (!this.video) return;
    this.target = this.querySelector("tooltip-target");

    // The film is pinned, so it only stops being visible once the page sheet
    // has covered it. It pauses then, to save decoding. On the way, the
    // labels fade out once the visible strip gets short.
    this.sheet = this.parentElement.querySelector(".StudioFilm-sheet");
    addEventListener("scroll", this, { passive: true });
    this.updatePlayback();

    this.timecode = this.sheet?.querySelector(".StudioFilm-timecode");
    this.addEventListener("click", this);
    this.watchFrames();
  }

  disconnectedCallback() {
    removeEventListener("scroll", this);
    if (this.frameCallback) this.video.cancelVideoFrameCallback?.(this.frameCallback);
  }

  handleEvent(e) {
    if (e.type === "scroll") {
      this.updatePlayback();
    } else if (e.type === "click") {
      this.togglePaused();
    }
  }

  // Pauses or plays the film at the viewer's request, and updates the
  // tooltip under the cursor to match.
  togglePaused() {
    this.toggleAttribute("paused");
    const paused = this.hasAttribute("paused");
    if (paused) this.video.pause();
    else if (!this.covered) this.video.play().catch(() => {});

    this.target?.setAttribute("tooltip", paused ? "Play" : "Pause");
  }

  updatePlayback() {
    if (!this.sheet) return;
    const visible = this.sheet.getBoundingClientRect().top - this.getBoundingClientRect().top;
    this.toggleAttribute("short", visible < MIN_VISIBLE);
    const covered = visible <= 0;
    if (covered === this.covered) return;
    this.covered = covered;
    if (covered) this.video.pause();
    else if (!this.hasAttribute("paused")) this.video.play().catch(() => {});
  }

  watchFrames() {
    if (!this.timecode) return;
    if (!("requestVideoFrameCallback" in HTMLVideoElement.prototype)) {
      this.video.addEventListener("timeupdate", () => {
        this.timecode.textContent = timecode(this.video.currentTime);
      });
      return;
    }
    const onFrame = (_now, metadata) => {
      this.timecode.textContent = timecode(metadata.mediaTime);
      this.frameCallback = this.video.requestVideoFrameCallback(onFrame);
    };
    this.frameCallback = this.video.requestVideoFrameCallback(onFrame);
  }
}

customElements.define("studio-film", StudioFilmElement);
